import { isVersionGte } from '@consumidor-positivo/ts-utils';

import { useUserContext } from '@common/contexts/UserContext';
import { addCookie, getCookie } from '@libs/utils/helpers/getSafeCookies';
import { isDev } from '@libs/utils/helpers/isDev';
import { isUserFromWebView } from '@libs/utils/helpers/webviewComunication';

const RENEG_APP_BASE_URL = import.meta.env.VITE_RENEG_APP_URL;

/** Minimum native app version that supports the reneg-app webview flow. */
const MIN_APP_VERSION = '7.8.3';

/**
 * Cookie used to hand the auth code off to the reneg-app. Scoped to the parent
 * domain (via `addCookie`), so the reneg-app subdomain can read it. Consumed and
 * deleted by the reneg-app right after authentication.
 */
const AUTH_HANDOFF_COOKIE = 'reneg-auth-code';

/** Short TTL for the handoff cookie — it only needs to survive the redirect. */
const AUTH_HANDOFF_TTL_MS = 5 * 60 * 1000;

export const getRenegAppUrl = (tab?: 'debts' | 'agreements') => {
  const params = new URLSearchParams();
  if (tab) params.set('tab', tab);
  const query = params.toString();
  return query ? `${RENEG_APP_BASE_URL}/?${query}` : `${RENEG_APP_BASE_URL}/`;
};

const useRenegAppExperiment = () => {
  const { getTokens } = useUserContext();
  const testCookieName = 'dx-reneg-app-cp';
  const isDevOrStg = isDev() || window.location.hostname.includes('stg');

  const variants = {
    control: 'control',
    renegApp: 'reneg-app',
  };

  /** True when the native app version is recent enough (dev/stg bypasses the gate). */
  const meetsMinAppVersion = () => {
    if (isDevOrStg) return true;
    const appVersion = getCookie('app-version', false);
    return !!appVersion && isVersionGte(appVersion, MIN_APP_VERSION);
  };

  const isRenegAppVariant = () => {
    const isInApp = isUserFromWebView() || window.isNativeApp;
    if (!isInApp && !isDevOrStg) return false;

    if (!meetsMinAppVersion()) return false;

    const currentResult = getCookie(testCookieName, false);
    if (!currentResult) return false;
    return currentResult.includes(variants.renegApp);
  };

  const getRedirectUrl = (tab?: 'debts' | 'agreements'): string => {
    if (isRenegAppVariant()) {
      const { accessToken } = getTokens();
      // Hand the auth code off via a parent-domain cookie instead of the URL, so
      // the JWT never lands in browser history, referrer headers or access logs.
      addCookie({
        key: AUTH_HANDOFF_COOKIE,
        value: accessToken,
        expires: new Date(Date.now() + AUTH_HANDOFF_TTL_MS),
      });
      if (isDevOrStg) addCookie({ key: 'app-version', value: MIN_APP_VERSION });
      return getRenegAppUrl(tab);
    }
    return tab === 'agreements' ? '/meus-acordos' : '/minhas-dividas';
  };

  /** QA-only: force the reneg-app variant cookie and reload. */
  const forceRenegAppVariant = () => {
    addCookie({ key: testCookieName, value: variants.renegApp });
    window.location.reload();
  };

  return { isRenegAppVariant, getRedirectUrl, forceRenegAppVariant };
};

export default useRenegAppExperiment;
